// Backend response envelope contract — see `src/core/contracts/api-response.ts`
// for the authoritative producer side. The backend emits:
//
//   ok    → { data: T,   meta: { timestamp, ...extra } }
//   list  → { data: T[], meta: { timestamp, count, limit, offset, ...extra } }
//   fail  → { error: CODE, message: string, details? }
//
// Earlier FE drafts of this app wrapped success with `{ ok: true, data }` and
// fail with `{ ok: false, error: { code, message } }`. Those wrappers never
// shipped on the backend; both shapes are accepted below so any historical
// hand-rolled fixture keeps parsing while real BE responses hit the canonical
// path. Anything else is a backend bug — surfaced as `ApiError`.
//
// PR-LP2 fix
// ----------
// Before this PR `unwrapOk` checked `'ok' in envelope && envelope.ok === true`
// and rejected the canonical `{ data, meta }` shape with `ENVELOPE_INVALID`.
// `useApprovalState` would therefore throw on every successful BE response,
// which meant `/approvazione` rendered the error state for any real
// PENDING_APPROVAL user. The new code recognises the canonical shape first
// and treats `ok: true` as a tolerated legacy marker rather than a hard gate.
// `isFailEnvelope` likewise now matches the canonical `{ error: 'CODE', message }`
// alongside the legacy `{ ok: false, error: { code, message } }`.

export interface OkEnvelope<T> {
  data: T;
  meta?: Record<string, unknown>;
  /** Legacy tag — present on hand-rolled envelopes, absent on canonical responses. */
  ok?: true;
}

export interface ListEnvelope<T> {
  data?: T[];
  meta?: {
    total?: number;
    count?: number;
    cursor?: string | null;
    nextCursor?: string | null;
    limit?: number;
    offset?: number;
    hasMore?: boolean;
    [key: string]: unknown;
  };
  /** Legacy tag — canonical backend responses omit the `ok` flag. */
  ok?: true;
  /** Legacy field — early FE draft stored the array under `list`. */
  list?: T[];
}

export interface FailEnvelope {
  error:
    | string
    | {
        code: string;
        message: string;
        details?: unknown;
      };
  message?: string;
  details?: unknown;
  /** Legacy tag — canonical backend responses omit the `ok` flag. */
  ok?: false;
}

export type Envelope<T> = OkEnvelope<T> | ListEnvelope<T> | FailEnvelope;

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;

  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isFailEnvelope(envelope: unknown): envelope is FailEnvelope {
  if (!isRecord(envelope)) return false;
  // Legacy `{ ok: false, ... }` wins immediately — historic compatibility.
  if (envelope['ok'] === false) return true;
  // Canonical: `{ error: 'CODE', message, details? }` — `error` is a string.
  const err = envelope['error'];
  if (typeof err === 'string' && err.length > 0) return true;
  // Tolerated middle form: `{ error: { code, message } }` (no `ok`).
  if (isRecord(err) && typeof err['code'] === 'string') return true;
  return false;
}

/**
 * Translate a fail envelope (any of the recognised shapes) into the
 * `ApiError` consumers branch on. Preserves the backend's own error
 * code as `ApiError.code` so a caller can still discriminate on the
 * domain-level error (e.g. `USER_PROVISIONING_TOKEN_EXPIRED`) while
 * `ApiError.status` carries the HTTP class for layer-agnostic checks.
 */
function failEnvelopeToApiError(envelope: unknown, status: number): ApiError {
  const backend = isRecord(envelope) ? (envelope as Record<string, unknown>) : {};
  const rawErr = backend['error'];
  let code = 'UNKNOWN_ERROR';
  let message: string | undefined;
  let details: unknown = backend['details'];
  if (typeof rawErr === 'string' && rawErr.length > 0) {
    code = rawErr;
    if (typeof backend['message'] === 'string') {
      message = backend['message'] as string;
    }
  } else if (isRecord(rawErr) && typeof rawErr['code'] === 'string') {
    code = rawErr['code'] as string;
    if (typeof rawErr['message'] === 'string') {
      message = rawErr['message'] as string;
    }
    if (rawErr['details'] !== undefined) {
      details = rawErr['details'];
    }
  }
  return new ApiError(
    code,
    message && message.length > 0 ? message : 'Risposta del server non valida',
    status,
    details,
  );
}

export function unwrapOk<T>(envelope: Envelope<T>, status: number): T {
  if (isFailEnvelope(envelope)) {
    throw failEnvelopeToApiError(envelope, status);
  }
  if (isRecord(envelope) && 'data' in envelope) {
    return (envelope as { data: T }).data;
  }
  throw new ApiError('ENVELOPE_INVALID', 'Unexpected response envelope shape', status, envelope);
}

export interface ListResult<T> {
  list: T[];
  meta: NonNullable<ListEnvelope<T>['meta']>;
}

export function unwrapList<T>(envelope: Envelope<T>, status: number): ListResult<T> {
  if (isFailEnvelope(envelope)) {
    throw failEnvelopeToApiError(envelope, status);
  }
  if (isRecord(envelope)) {
    const data = (envelope as { data?: unknown }).data;
    if (Array.isArray(data)) {
      const meta = (envelope as { meta?: ListResult<T>['meta'] }).meta ?? {};
      return { list: data as T[], meta };
    }
    // Legacy FE draft stored the array under `list`.
    const legacyList = (envelope as { list?: unknown }).list;
    if (Array.isArray(legacyList)) {
      const meta = (envelope as { meta?: ListResult<T>['meta'] }).meta ?? {};
      return { list: legacyList as T[], meta };
    }
  }
  throw new ApiError('ENVELOPE_INVALID', 'Unexpected list envelope shape', status, envelope);
}
